import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X,
  Check,
  Mail,
  FileText,
  Calendar,
  FolderOpen,
  Loader2,
  Info,
} from 'lucide-react';
import { useThemeStore } from '../../store/theme';
import type { ConnectorProvider, ConnectorAction } from '../../types';

interface PendingAction {
  id: number;
  provider: ConnectorProvider;
  providerName: string;
  action: ConnectorAction;
  preview: Record<string, unknown>;
  actionData: Record<string, unknown>;
  requiresApproval: boolean;
}

interface ConnectorEditModalProps {
  isOpen: boolean;
  action: PendingAction | null;
  onClose: () => void;
  onSave: (id: number, editedData: Record<string, unknown>) => void;
  isLoading?: boolean;
}

// Provider icons mapping
const PROVIDER_ICONS: Record<ConnectorProvider, typeof Mail> = {
  gmail: Mail,
  google_docs: FileText,
  google_calendar: Calendar,
  google_drive: FolderOpen,
  trello: FileText,
};

// Provider colors
const PROVIDER_COLORS: Record<ConnectorProvider, { bg: string; text: string }> = {
  gmail: { bg: 'bg-red-500/10', text: 'text-red-500' },
  google_docs: { bg: 'bg-blue-500/10', text: 'text-blue-500' },
  google_calendar: { bg: 'bg-green-500/10', text: 'text-green-500' },
  google_drive: { bg: 'bg-yellow-500/10', text: 'text-yellow-500' },
  trello: { bg: 'bg-indigo-500/10', text: 'text-indigo-500' },
};

// Action labels
const ACTION_LABELS: Record<string, string> = {
  send_email: 'Send Email',
  create_draft: 'Create Draft',
  create_event: 'Create Calendar Event',
  update_event: 'Update Calendar Event',
  create_doc: 'Create Document',
  update_doc: 'Update Document',
  create_card: 'Create Trello Card',
  update_card: 'Update Trello Card',
  move_card: 'Move Trello Card',
  add_comment: 'Add Comment',
};

// Field configurations for different actions
const FIELD_CONFIGS: Record<string, Array<{
  key: string;
  label: string;
  type: 'text' | 'textarea' | 'email' | 'datetime-local' | 'select';
  required?: boolean;
  placeholder?: string;
  options?: string[];
  helpText?: string;
}>> = {
  send_email: [
    { key: 'to', label: 'To', type: 'email', required: true, placeholder: 'recipient@example.com' },
    { key: 'cc', label: 'CC', type: 'email', placeholder: 'cc@example.com (optional)' },
    { key: 'bcc', label: 'BCC', type: 'email', placeholder: 'bcc@example.com (optional)' },
    { key: 'subject', label: 'Subject', type: 'text', required: true, placeholder: 'Email subject' },
    { key: 'body', label: 'Body', type: 'textarea', required: true, placeholder: 'Email content...' },
  ],
  create_draft: [
    { key: 'to', label: 'To', type: 'email', placeholder: 'recipient@example.com (optional)' },
    { key: 'subject', label: 'Subject', type: 'text', placeholder: 'Draft subject' },
    { key: 'body', label: 'Body', type: 'textarea', placeholder: 'Draft content...' },
  ],
  create_event: [
    { key: 'summary', label: 'Event Title', type: 'text', required: true, placeholder: 'Meeting title' },
    { key: 'description', label: 'Description', type: 'textarea', placeholder: 'Event description (optional)' },
    { key: 'start', label: 'Start Time', type: 'datetime-local', required: true },
    { key: 'end', label: 'End Time', type: 'datetime-local', required: true },
    { key: 'location', label: 'Location', type: 'text', placeholder: 'Event location (optional)' },
  ],
  update_event: [
    { key: 'summary', label: 'Event Title', type: 'text', placeholder: 'New title (optional)' },
    { key: 'description', label: 'Description', type: 'textarea', placeholder: 'New description (optional)' },
    { key: 'start', label: 'Start Time', type: 'datetime-local' },
    { key: 'end', label: 'End Time', type: 'datetime-local' },
    { key: 'location', label: 'Location', type: 'text', placeholder: 'New location (optional)' },
  ],
  create_doc: [
    { key: 'title', label: 'Document Title', type: 'text', required: true, placeholder: 'Untitled Document' },
    { key: 'content', label: 'Content', type: 'textarea', placeholder: 'Document content...' },
  ],
  update_doc: [
    { key: 'content', label: 'New Content', type: 'textarea', required: true, placeholder: 'Updated content...', helpText: 'This will replace the current document content' },
  ],
  create_card: [
    { key: 'name', label: 'Card Title', type: 'text', required: true, placeholder: 'Task name' },
    { key: 'desc', label: 'Description', type: 'textarea', placeholder: 'Task description (optional)' },
    { key: 'due', label: 'Due Date', type: 'datetime-local', placeholder: 'Due date (optional)' },
  ],
  update_card: [
    { key: 'name', label: 'Card Title', type: 'text', placeholder: 'New title (optional)' },
    { key: 'desc', label: 'Description', type: 'textarea', placeholder: 'New description (optional)' },
    { key: 'due', label: 'Due Date', type: 'datetime-local' },
  ],
  move_card: [
    { key: 'listId', label: 'Target List ID', type: 'text', required: true, placeholder: 'List ID to move to' },
    { key: 'pos', label: 'Position', type: 'select', options: ['top', 'bottom'] },
  ],
  add_comment: [
    { key: 'text', label: 'Comment', type: 'textarea', required: true, placeholder: 'Enter your comment...' },
  ],
};

export default function ConnectorEditModal({
  isOpen,
  action,
  onClose,
  onSave,
  isLoading = false,
}: ConnectorEditModalProps) {
  const { theme } = useThemeStore();
  const isDark = theme === 'dark';
  const [formData, setFormData] = useState<Record<string, unknown>>({});

  // Initialize form data when action changes
  useEffect(() => {
    if (action) {
      setFormData({ ...action.actionData });
    }
  }, [action]);

  if (!action) return null;

  const Icon = PROVIDER_ICONS[action.provider] || Mail;
  const colors = PROVIDER_COLORS[action.provider] || PROVIDER_COLORS.gmail;
  const actionLabel = ACTION_LABELS[action.action] || action.action;
  const fields = FIELD_CONFIGS[action.action] || [];

  const handleInputChange = (key: string, value: string) => {
    setFormData(prev => ({ ...prev, [key]: value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(action.id, formData);
  };

  const formatDatetimeValue = (value: unknown): string => {
    if (!value) return '';
    if (typeof value === 'string') {
      // Convert ISO string to datetime-local format
      try {
        const date = new Date(value);
        return date.toISOString().slice(0, 16);
      } catch {
        return value;
      }
    }
    return '';
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50"
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
          >
            <div
              className={`relative w-full max-w-lg max-h-[90vh] overflow-hidden rounded-2xl ${
                isDark ? 'bg-dark-800 border border-dark-600' : 'bg-white shadow-xl'
              }`}
            >
              {/* Header */}
              <div className={`sticky top-0 z-10 flex items-center justify-between p-4 border-b ${
                isDark ? 'border-dark-600 bg-dark-800' : 'border-slate-200 bg-white'
              }`}>
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${colors.bg}`}>
                    <Icon className={`w-5 h-5 ${colors.text}`} />
                  </div>
                  <div>
                    <h2 className={`font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                      Edit {actionLabel}
                    </h2>
                    <p className={`text-sm ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>
                      {action.providerName}
                    </p>
                  </div>
                </div>
                <button
                  onClick={onClose}
                  className={`p-2 rounded-lg transition-colors ${
                    isDark
                      ? 'hover:bg-dark-700 text-text-muted hover:text-white'
                      : 'hover:bg-slate-100 text-slate-400 hover:text-slate-600'
                  }`}
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Form */}
              <form onSubmit={handleSubmit} className="overflow-y-auto max-h-[calc(90vh-140px)]">
                <div className="p-4 space-y-4">
                  {fields.length === 0 ? (
                    // Show raw JSON editor for unknown actions
                    <div>
                      <label className={`block text-sm font-medium mb-2 ${
                        isDark ? 'text-white' : 'text-slate-900'
                      }`}>
                        Action Parameters (JSON)
                      </label>
                      <textarea
                        value={JSON.stringify(formData, null, 2)}
                        onChange={(e) => {
                          try {
                            const parsed = JSON.parse(e.target.value);
                            setFormData(parsed);
                          } catch {
                            // Keep current data if JSON is invalid
                          }
                        }}
                        rows={8}
                        className={`w-full px-4 py-3 rounded-xl border font-mono text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-accent-purple ${
                          isDark
                            ? 'bg-dark-700 border-dark-600 text-white placeholder-text-muted'
                            : 'bg-white border-slate-200 text-slate-900 placeholder-slate-400'
                        }`}
                      />
                    </div>
                  ) : (
                    fields.map((field) => (
                      <div key={field.key}>
                        <label className={`block text-sm font-medium mb-2 ${
                          isDark ? 'text-white' : 'text-slate-900'
                        }`}>
                          {field.label}
                          {field.required && <span className="text-red-500 ml-1">*</span>}
                        </label>

                        {field.type === 'textarea' ? (
                          <textarea
                            value={(formData[field.key] as string) || ''}
                            onChange={(e) => handleInputChange(field.key, e.target.value)}
                            placeholder={field.placeholder}
                            required={field.required}
                            rows={4}
                            className={`w-full px-4 py-3 rounded-xl border transition-colors focus:outline-none focus:ring-2 focus:ring-accent-purple ${
                              isDark
                                ? 'bg-dark-700 border-dark-600 text-white placeholder-text-muted'
                                : 'bg-white border-slate-200 text-slate-900 placeholder-slate-400'
                            }`}
                          />
                        ) : field.type === 'select' ? (
                          <select
                            value={(formData[field.key] as string) || ''}
                            onChange={(e) => handleInputChange(field.key, e.target.value)}
                            required={field.required}
                            className={`w-full px-4 py-3 rounded-xl border transition-colors focus:outline-none focus:ring-2 focus:ring-accent-purple ${
                              isDark
                                ? 'bg-dark-700 border-dark-600 text-white'
                                : 'bg-white border-slate-200 text-slate-900'
                            }`}
                          >
                            <option value="">Select...</option>
                            {field.options?.map((opt) => (
                              <option key={opt} value={opt}>
                                {opt.charAt(0).toUpperCase() + opt.slice(1)}
                              </option>
                            ))}
                          </select>
                        ) : field.type === 'datetime-local' ? (
                          <input
                            type="datetime-local"
                            value={formatDatetimeValue(formData[field.key])}
                            onChange={(e) => handleInputChange(field.key, e.target.value)}
                            required={field.required}
                            className={`w-full px-4 py-3 rounded-xl border transition-colors focus:outline-none focus:ring-2 focus:ring-accent-purple ${
                              isDark
                                ? 'bg-dark-700 border-dark-600 text-white'
                                : 'bg-white border-slate-200 text-slate-900'
                            }`}
                          />
                        ) : (
                          <input
                            type={field.type}
                            value={(formData[field.key] as string) || ''}
                            onChange={(e) => handleInputChange(field.key, e.target.value)}
                            placeholder={field.placeholder}
                            required={field.required}
                            className={`w-full px-4 py-3 rounded-xl border transition-colors focus:outline-none focus:ring-2 focus:ring-accent-purple ${
                              isDark
                                ? 'bg-dark-700 border-dark-600 text-white placeholder-text-muted'
                                : 'bg-white border-slate-200 text-slate-900 placeholder-slate-400'
                            }`}
                          />
                        )}

                        {field.helpText && (
                          <p className={`flex items-center gap-1 mt-1.5 text-xs ${
                            isDark ? 'text-text-muted' : 'text-slate-500'
                          }`}>
                            <Info className="w-3 h-3" />
                            {field.helpText}
                          </p>
                        )}
                      </div>
                    ))
                  )}
                </div>

                {/* Footer */}
                <div className={`sticky bottom-0 flex items-center justify-end gap-3 p-4 border-t ${
                  isDark ? 'border-dark-600 bg-dark-800' : 'border-slate-200 bg-white'
                }`}>
                  <button
                    type="button"
                    onClick={onClose}
                    disabled={isLoading}
                    className={`px-4 py-2 rounded-lg font-medium text-sm transition-colors ${
                      isDark
                        ? 'bg-dark-700 text-white hover:bg-dark-600'
                        : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                    }`}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isLoading}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-sm transition-all ${
                      isLoading ? 'opacity-50 cursor-not-allowed' : 'hover:opacity-90'
                    } bg-accent-purple text-white`}
                  >
                    {isLoading ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Check className="w-4 h-4" />
                    )}
                    Save & Approve
                  </button>
                </div>
              </form>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
